import { Button, ButtonType, Form, FormGroup, Input, InputType, Select, SubmitButton } from '@digicert/dcone-common-ui'
import { EMAIL_REGEXP, NAME_REGEXP, RouterConfig, USER_NAME_REGEXP, USER_NAME_WITH_SPACE_REGEXP } from 'core/utils/constants'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { Translate } from 'react-localize-redux'
import { mapOptionsCommonUi, translate } from 'shared/helpers/utils'
import styles from './UpdateUser.module.scss'
import { IAppState } from 'core/store/store'
import { checkIfUsernameExists } from 'public-pages/self-service-user/SelfServiceUser.actions'
import { connect } from 'react-redux'
import { IAccessScope } from 'reducers/Reducers.interfaces'
import { fetchUserById } from 'core/actions/Users.actions'
import { useHistory } from 'react-router-dom'

const UpdateUserDetails = ({ user, onCancel, onEdit, checkIfUsernameExists, profile, localeList, fetchUserById }) => {
  const [userNameExists, setUserNameExists] = useState(false)
  const [userData, setUserData] = useState(user);
  const [phoneNumber, setPhoneNumber] = useState(user.phone || '')
  const [userNameRegex, setUserNameRegex] = useState(user.user_name.includes(" ") ? USER_NAME_WITH_SPACE_REGEXP : USER_NAME_REGEXP)
  const [samlNameExists, setSamlNameExists] = useState(false)
  const [oidcNameExists, setOidcNameExists] = useState(false)
  const history = useHistory()

  useEffect(() => {
    if(history.location.pathname === RouterConfig.administrators.table) { //call the user api only from the user list page
      fetchUserById(user.id).then((response: any) => {
        if (response.user_name.includes(" ")) {
          setUserNameRegex(USER_NAME_WITH_SPACE_REGEXP)
        }
        setUserData({ ...response })
      })
    }
  }, [user])

  useEffect(() => {
    if (user) {
      setUserData({ ...user })
      setPhoneNumber(user.phone || '')
      setUserNameExists(false)
      setSamlNameExists(false)
      setOidcNameExists(false)
    }
  }, [onCancel])

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUserData({ ...userData, [event.target.name]: event.target.value })
  }

  const onPhoneChange = (value) => {
    const phoneNo = value || ''
    setPhoneNumber(phoneNo)
    setUserData({ ...userData, 'phone': phoneNo })
  }

  const updateUserRequest = (event) => {
    event.preventDefault()
    onEdit(event, user.groups.map(group => group.id), userData)
  }

  const onUserNameBlur = (event: ChangeEvent<HTMLInputElement>) => {
    onInputChange(event)
    checkIfUsernameExists(event.target.value, '', 'userName', userData.id).then((response: boolean) => {
      setUserNameExists(response)
    })
  }

  const validateUserName = (): any => {
    if (userNameExists) {
      return translate('notifications.exceptions.USERNAME_EXISTS');
    }
  }

  const onSamlUserNameBlur = (event: ChangeEvent<HTMLInputElement>) => {
    checkIfUsernameExists(event.target.value, '', 'ssoUserName', user.id).then((response: boolean) => {
      setSamlNameExists(response)
    })
  }

  const validateSamlUserName = (): any => {
    if (samlNameExists) {
      return translate('notifications.exceptions.USERNAME_EXISTS');
    }
  }

  const onOidcUserNameBlur = (event: ChangeEvent<HTMLInputElement>) => {
    checkIfUsernameExists(event.target.value, '', 'oidcUserName', user.id).then((response: boolean) => {
      setOidcNameExists(response)
    })
  }

  const validateOidcUserName = (): any => {
    if (oidcNameExists) {
      return translate('notifications.exceptions.USERNAME_EXISTS');
    }
  }

  const onLangChange = (lang: any): void => {
    setUserData({ ...userData, 'locale': lang })
  }

  const validateLangSelect = (): any => !userData?.locale ? translate('common.form.validation.required') : ''

  return (
    <section className={styles.updateUser}>
      <Form center={false} onSubmit={(event) => { updateUserRequest(event) }} className={styles.form}>
        <div>
          <FormGroup>
            <div className={styles.nameGroup}>
              <Input
                name='first_name'
                label={translate('common.form.firstName')}
                required
                minLength={1}
                maxLength={256}
                value={userData.first_name}
                onChange={onInputChange}
                pattern={NAME_REGEXP}
                errors={{
                  valueMissing: translate('common.form.validation.required')
                }}
                id='first_name'
              />

              <Input
                name='last_name'
                label={translate('common.form.lnOptional')}
                maxLength={256}
                value={userData.last_name}
                pattern={NAME_REGEXP}
                onChange={onInputChange}
                id='last_name'
              />
            </div>
          </FormGroup>

          <FormGroup>
            <Input
              type={InputType.EMAIL}
              name='email'
              label={<Translate id='common.form.emailAddress' />}
              required
              value={userData.email}
              onChange={onInputChange}
              pattern={EMAIL_REGEXP}
              errors={{
                typeMismatch: translate('notifications.errors.invalidEmailError'),
                valueMissing: translate('common.form.validation.required'),
                patternMismatch: translate('notifications.errors.invalidEmailError')
              }}
            />
          </FormGroup>

          <FormGroup>
            <Input
              name='user_name'
              label={<Translate id='common.form.username' />}
              required
              value={userData.user_name}
              pattern={userNameRegex}
              errors={{
                valueMissing: translate('common.form.validation.required'),
                patternMismatch: translate('common.form.validation.invalidUserName')
              }}
              onChange={onInputChange}
              validate={validateUserName}
              onBlur={(event)=> onUserNameBlur(event)}
            />
          </FormGroup>

          {user.sign_in_method.signInMethod === 'singleSignOnSaml' && user.sign_in_method.status === 'enabled' && (
            <FormGroup>
              <Input
                name='saml_username'
                label={<Translate id='common.form.samlUsername' />}
                required
                value={userData.saml_username}
                validate={validateSamlUserName}
                onChange={onInputChange}
                onBlur={(event) => onSamlUserNameBlur(event)}
                pattern={USER_NAME_REGEXP}
                disabled={profile?.access_scope !== IAccessScope.system}
                errors={{
                  valueMissing: translate('common.form.validation.required'),
                  patternMismatch: translate('common.form.validation.invalidUserName')
                }}
              />
            </FormGroup>
          )}
          {user.sign_in_method.signInMethod === 'singleSignOnOidc' && user.sign_in_method.status === 'enabled' && (
            <FormGroup>
              <Input
                name='oidc_username'
                label={<Translate id='common.form.oidcUsername' />}
                required
                value={userData.oidc_username}
                pattern={USER_NAME_REGEXP}
                disabled={profile?.access_scope !== IAccessScope.system}
                errors={{
                  valueMissing: translate('common.form.validation.required'),
                  patternMismatch: translate('common.form.validation.invalidUserName')
                }}
                onChange={onInputChange}
                onBlur={(event) => onOidcUserNameBlur(event)}
                validate={validateOidcUserName}
              />
            </FormGroup>
          )}

          <FormGroup>
            <Input
              label={translate('common.form.phoneOptional')}
              type={InputType.TELEPHONE}
              validateChanges
              value={phoneNumber}
              errors={{ valueMissing: translate('common.form.validation.required') }}
              onChange={onPhoneChange}
            />
          </FormGroup>

          {user.other_phone && <FormGroup>
            <Input
              label={<Translate id='common.form.otherPhoneOptional' />}
              value={userData.other_phone}
              onChange={onInputChange}
              name='other_phone'
            />
          </FormGroup>}
          <FormGroup>
            <Select
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
              allowClear
              defaultValue={userData.locale}
              value={userData.locale}
              label={translate('profile.defaultLanguage')}
              onChange={onLangChange}
              options={mapOptionsCommonUi(localeList?.locales)}
              errors={{ valueMissing: translate('common.form.validation.required') }}
              validate={validateLangSelect}
              optionFilterProp="label"
              showSearch
            />
          </FormGroup>
        </div>

        <div className={styles.formActions}>
          <Button buttonType={ButtonType.SECONDARY} onClick={onCancel}>
            <Translate id='common.form.buttons.cancelBtn' />
          </Button>

          <SubmitButton>
            {translate('common.form.buttons.updateUserBtn')}
          </SubmitButton>
        </div>
      </Form>
    </section>
  )
}

export default connect((state: IAppState) => ({
  profile: state.profile.data,
  localeList: state.locales,
}),
  { checkIfUsernameExists,
    fetchUserById
   }
)(UpdateUserDetails)
